import { motion, useInView } from "framer-motion";
import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "../../../components/feature/Navbar";
import Footer from "../../../components/feature/Footer";
import useSEO from "../../../hooks/useSEO";

const SemanticArchitecture = () => {
  const heroRef = useRef(null);
  const isHeroInView = useInView(heroRef, { once: true });
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);

  // SEO dinámico
  useSEO({
    title: "Arquitectura Semántica y Topic Clusters | SEOgenia",
    description: "Construimos la estructura perfecta con HTML5 semántico y jerarquías lógicas para que buscadores e IAs comprendan tu contenido al 100%. Mejora tu indexación y rankings.",
    keywords: "arquitectura semántica, html5 semántico, topic clusters, estructura web, jerarquía seo, etiquetas semánticas",
    canonical: "https://seogenia.es/servicios/arquitectura-semantica",
    structuredData: {
      "@context": "https://schema.org",
      "@type": "Service",
      "name": "Arquitectura Semántica y Topic Clusters",
      "description": "Construcción de estructura web perfecta con HTML5 semántico y jerarquías lógicas",
      "provider": {
        "@type": "Organization",
        "name": "SEOgenia",
        "url": "https://seogenia.es"
      },
      "areaServed": "ES",
      "serviceType": "SEO Técnico"
    }
  });

  const faqs = [
    {
      question: "¿Qué es exactamente la arquitectura semántica web?",
      answer: "La arquitectura semántica es la forma en que estructuramos el código HTML de tu sitio web usando etiquetas HTML5 semánticas (header, nav, main, article, section, aside, footer) y jerarquías lógicas. Esto permite que los motores de búsqueda y agentes de IA comprendan perfectamente el contexto, la relevancia y las relaciones entre tu contenido, mejorando significativamente tu posicionamiento."
    },
    {
      question: "¿Cuánto tiempo tarda en implementarse una arquitectura semántica?",
      answer: "Depende del tamaño de tu sitio web. Para un sitio pequeño (10-20 páginas), el proceso completo toma entre 2-3 semanas. Para sitios medianos (50-100 páginas), entre 4-6 semanas. Incluye auditoría, diseño estratégico, implementación y validación. Los resultados en rankings comienzan a verse entre 4-8 semanas después de la implementación."
    },
    {
      question: "¿Mi sitio actual necesita una reestructuración completa?",
      answer: "No necesariamente. Primero realizamos una auditoría profunda para identificar qué elementos ya están bien estructurados y cuáles necesitan mejoras. En muchos casos, podemos optimizar la arquitectura existente sin necesidad de reconstruir todo desde cero, minimizando el impacto en tu sitio en producción."
    },
    {
      question: "¿La arquitectura semántica mejora la velocidad de carga?",
      answer: "Sí, indirectamente. Una arquitectura semántica bien implementada utiliza código HTML más limpio y eficiente, eliminando divs innecesarios y estructuras redundantes. Esto reduce el tamaño del HTML, mejora el parsing del navegador y contribuye a mejores Core Web Vitals, especialmente en métricas como FCP (First Contentful Paint) y LCP (Largest Contentful Paint)."
    },
    {
      question: "¿Cómo afecta esto a mi posicionamiento en Google?",
      answer: "La arquitectura semántica es un factor de ranking indirecto pero muy importante. Google puede rastrear e indexar tu contenido más eficientemente, comprende mejor el contexto de cada página, identifica correctamente tu contenido principal vs secundario, y mejora tus posibilidades de aparecer en featured snippets. Clientes típicos ven mejoras de 15-30% en rankings orgánicos en 3-6 meses."
    },
    {
      question: "¿Es compatible con mi CMS actual (WordPress, Shopify, etc.)?",
      answer: "Sí, completamente. Trabajamos con todos los CMS principales: WordPress, Shopify, Wix, Webflow, y desarrollos custom. Adaptamos la implementación a las capacidades y limitaciones de cada plataforma, utilizando temas personalizados, plugins especializados o código custom según sea necesario."
    },
    {
      question: "¿Qué diferencia hay entre arquitectura semántica y Schema Markup?",
      answer: "Son complementarios pero diferentes. La arquitectura semántica estructura el HTML visible de tu página (header, nav, article, etc.) para que navegadores y buscadores entiendan la jerarquía visual. Schema Markup son datos estructurados invisibles (JSON-LD) que añaden contexto adicional sobre el significado del contenido. Ambos son esenciales para un SEO técnico completo."
    },
    {
      question: "¿Los agentes de IA como ChatGPT entienden mejor mi contenido con esto?",
      answer: "Absolutamente. Los agentes de IA (ChatGPT, Gemini, Perplexity, Claude) utilizan la arquitectura semántica para identificar qué partes de tu contenido son más relevantes, entender la jerarquía de información, y extraer respuestas precisas. Una buena arquitectura semántica aumenta significativamente las probabilidades de que tu contenido sea citado como fuente en respuestas de IA."
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <Navbar scrolled={true} />

      {/* Hero Section */}
      <section
        ref={heroRef}
        className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-slate-900"
      >
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isHeroInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-center max-w-4xl mx-auto"
          >
            <div className="inline-flex items-center space-x-2 bg-white/10 px-4 py-2 rounded-full shadow-sm mb-6">
              <i className="ri-code-s-slash-line text-cyan-400"></i>
              <span className="text-sm font-medium text-white">Servicio Premium</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
              Arquitectura Semántica
            </h1>
            <p className="text-lg sm:text-xl text-gray-300 mb-8 leading-relaxed">
              Construimos la estructura perfecta para que buscadores e IAs comprendan tu contenido al 100%. Una base sólida que multiplica tu visibilidad.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a
                href="#contacto"
                className="px-8 py-3 bg-cyan-500 text-white rounded-full font-medium hover:shadow-lg transition-all duration-300 whitespace-nowrap"
              >
                Solicitar Análisis
              </a>
              <a
                href="#beneficios"
                className="px-8 py-3 bg-white/10 text-white rounded-full font-medium hover:shadow-lg transition-all duration-300 border border-white/20 whitespace-nowrap"
              >
                Ver Beneficios
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* What is Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
                ¿Qué es la Arquitectura Semántica?
              </h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                Es el arte de estructurar tu sitio web usando HTML5 semántico y jerarquías lógicas que permiten a los motores de búsqueda y agentes de IA entender perfectamente el contexto, la relevancia y las relaciones entre tu contenido.
              </p>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                No se trata solo de código limpio, sino de crear un mapa mental digital que guía a Google, ChatGPT, Gemini y otros sistemas hacia la comprensión exacta de tu propuesta de valor.
              </p>
              <div className="flex flex-wrap gap-3">
                <span className="px-4 py-2 bg-cyan-100 text-cyan-700 rounded-full text-sm font-medium">HTML5 Semántico</span>
                <span className="px-4 py-2 bg-purple-100 text-purple-700 rounded-full text-sm font-medium">Jerarquía Clara</span>
                <span className="px-4 py-2 bg-pink-100 text-pink-700 rounded-full text-sm font-medium">Contexto Perfecto</span>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <img
                src="https://public.readdy.ai/ai/img_res/cc3bfcd255b8978a7dcf3bb21e027a23.jpg"
                alt="Arquitectura Semántica"
                className="w-full h-auto rounded-2xl shadow-2xl object-cover object-top"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section id="beneficios" className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Beneficios Clave
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Una arquitectura semántica bien diseñada transforma tu sitio en una máquina de posicionamiento
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: "ri-search-eye-line",
                title: "Mejor Indexación",
                description: "Los buscadores rastrean y comprenden tu contenido más rápido y profundamente",
                color: "cyan"
              },
              {
                icon: "ri-robot-2-line",
                title: "IA-Friendly",
                description: "ChatGPT, Gemini y otros agentes entienden perfectamente tu información",
                color: "purple"
              },
              {
                icon: "ri-line-chart-line",
                title: "Ranking Superior",
                description: "Mejora significativa en posiciones para palabras clave estratégicas",
                color: "pink"
              },
              {
                icon: "ri-speed-up-line",
                title: "Carga Optimizada",
                description: "Código limpio y eficiente que acelera la velocidad de tu sitio",
                color: "cyan"
              },
              {
                icon: "ri-accessibility-line",
                title: "Accesibilidad Total",
                description: "Cumple con estándares WCAG mejorando la experiencia de todos los usuarios",
                color: "purple"
              },
              {
                icon: "ri-shield-check-line",
                title: "Futuro-Proof",
                description: "Preparado para los próximos cambios en algoritmos y tecnologías",
                color: "pink"
              }
            ].map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300"
              >
                <div className={`w-14 h-14 bg-gradient-to-br from-${benefit.color}-500 to-${benefit.color}-600 rounded-xl flex items-center justify-center mb-6`}>
                  <i className={`${benefit.icon} text-2xl text-white`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{benefit.title}</h3>
                <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Nuestro Proceso
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Metodología probada para construir arquitecturas semánticas de alto rendimiento
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-4 gap-8">
            {[
              {
                step: "01",
                title: "Auditoría Profunda",
                description: "Analizamos tu estructura actual, identificamos problemas semánticos y oportunidades de mejora",
                icon: "ri-search-line"
              },
              {
                step: "02",
                title: "Diseño Estratégico",
                description: "Creamos un blueprint personalizado con jerarquías, etiquetas HTML5 y relaciones de contenido",
                icon: "ri-draft-line"
              },
              {
                step: "03",
                title: "Implementación",
                description: "Aplicamos la nueva arquitectura con código limpio, validado y optimizado para rendimiento",
                icon: "ri-code-box-line"
              },
              {
                step: "04",
                title: "Validación y Monitoreo",
                description: "Verificamos la correcta indexación y monitoreamos mejoras en rankings y visibilidad",
                icon: "ri-line-chart-line"
              }
            ].map((process, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="relative"
              >
                <div className="bg-gradient-to-br from-cyan-50 via-purple-50 to-pink-50 p-8 rounded-2xl h-full">
                  <div className="text-5xl font-bold bg-gradient-to-r from-cyan-600 via-purple-600 to-pink-600 bg-clip-text text-transparent mb-4">
                    {process.step}
                  </div>
                  <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-pink-500 rounded-lg flex items-center justify-center mb-4">
                    <i className={`${process.icon} text-xl text-white`}></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{process.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{process.description}</p>
                </div>
                {index < 3 && (
                  <div className="hidden lg:block absolute top-1/2 -right-4 transform -translate-y-1/2">
                    <i className="ri-arrow-right-line text-2xl text-gray-300"></i>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Technical Details */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Elementos Técnicos que Implementamos
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Cada detalle cuenta para lograr una arquitectura semántica perfecta
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {[
              {
                category: "Etiquetas HTML5 Semánticas",
                items: [
                  "header, nav, main, article, section, aside, footer",
                  "figure, figcaption para contenido multimedia",
                  "time para fechas y eventos",
                  "address para información de contacto",
                  "mark para resaltados importantes"
                ]
              },
              {
                category: "Jerarquía de Encabezados",
                items: [
                  "H1 único por página con keyword principal",
                  "H2-H6 en orden lógico descendente",
                  "Estructura de árbol coherente",
                  "Keywords secundarias en H2-H3",
                  "Subtemas organizados jerárquicamente"
                ]
              },
              {
                category: "Atributos ARIA",
                items: [
                  "role para definir roles de elementos",
                  "aria-label para descripciones accesibles",
                  "aria-describedby para relaciones",
                  "aria-hidden para contenido decorativo",
                  "aria-live para actualizaciones dinámicas"
                ]
              },
              {
                category: "Microformatos y Datos Estructurados",
                items: [
                  "Schema.org integrado en HTML",
                  "Breadcrumbs semánticos",
                  "Listas ordenadas y no ordenadas correctas",
                  "Tablas con thead, tbody, tfoot",
                  "Forms con labels y fieldsets apropiados"
                ]
              }
            ].map((section, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white p-8 rounded-2xl shadow-lg"
              >
                <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
                  <span className="w-8 h-8 bg-gradient-to-br from-cyan-500 to-pink-500 rounded-lg flex items-center justify-center mr-3">
                    <i className="ri-checkbox-circle-line text-white text-sm"></i>
                  </span>
                  {section.category}
                </h3>
                <ul className="space-y-3">
                  {section.items.map((item, idx) => (
                    <li key={idx} className="flex items-start">
                      <i className="ri-arrow-right-s-line text-cyan-600 mt-1 mr-2 flex-shrink-0"></i>
                      <span className="text-gray-600">{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-900">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Resultados Reales
            </h2>
            <p className="text-lg text-gray-300 max-w-3xl mx-auto">
              Empresas que mejoraron su SEO con arquitectura semántica
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {[
              {
                name: "Javier Morales",
                company: "E-commerce Fashion",
                role: "CTO",
                text: "La reestructuración semántica mejoró nuestra indexación un 85%. Google ahora entiende perfectamente nuestra jerarquía de productos y categorías.",
                rating: 5
              },
              {
                name: "Elena Ruiz",
                company: "Portal Noticias Digital",
                role: "Directora Técnica",
                text: "Pasamos de 40/100 a 95/100 en Lighthouse. Los featured snippets se multiplicaron por 5. La arquitectura HTML5 fue clave para este salto.",
                rating: 5
              }
            ].map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-gray-800 p-8 rounded-2xl border border-gray-700"
              >
                <div className="flex items-center gap-2 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-yellow-400 text-lg"></i>
                  ))}
                </div>
                <p className="text-lg text-white leading-relaxed mb-6 italic">
                  "{testimonial.text}"
                </p>
                <div>
                  <p className="font-bold text-white text-lg">
                    {testimonial.name}
                  </p>
                  <p className="text-gray-300">
                    {testimonial.role} en {testimonial.company}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Preguntas Frecuentes
            </h2>
            <p className="text-lg text-gray-600">
              Resolvemos tus dudas sobre arquitectura semántica
            </p>
          </motion.div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white border border-gray-200 rounded-2xl overflow-hidden hover:shadow-lg transition-all duration-300"
              >
                <button
                  onClick={() => setOpenFAQ(openFAQ === index ? null : index)}
                  className="w-full px-8 py-6 flex items-center justify-between text-left hover:bg-gray-50 transition-colors duration-200"
                >
                  <h3 className="text-lg font-bold text-gray-900 pr-8">
                    {faq.question}
                  </h3>
                  <i
                    className={`ri-arrow-down-s-line text-2xl text-cyan-600 flex-shrink-0 transition-transform duration-300 ${
                      openFAQ === index ? "rotate-180" : ""
                    }`}
                  ></i>
                </button>
                <div
                  className={`overflow-hidden transition-all duration-300 ${
                    openFAQ === index ? "max-h-96" : "max-h-0"
                  }`}
                >
                  <div className="px-8 pb-6">
                    <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-purple-600">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
              ¿Listo para una Arquitectura Semántica de Alto Rendimiento?
            </h2>
            <p className="text-lg text-white/90 mb-8 leading-relaxed">
              Construyamos juntos la base perfecta para tu éxito en buscadores e IAs
            </p>
            <Link
              to="/#contacto"
              className="inline-block px-8 py-4 bg-white text-gray-900 rounded-full font-bold hover:shadow-2xl transition-all duration-300 whitespace-nowrap"
            >
              Solicitar Consultoría Gratuita
            </Link>
          </motion.div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-20 bg-slate-900">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            ¿Listo para implementar una arquitectura semántica perfecta?
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            Descubre cómo podemos transformar la estructura de tu sitio web
          </p>
          <Link 
            to="/#contacto"
            className="inline-block px-8 py-4 bg-cyan-500 text-white rounded-full font-medium text-lg hover:shadow-2xl hover:scale-105 transition-all duration-300 whitespace-nowrap"
          >
            Solicitar Consulta Gratuita
          </Link>
        </div>
      </section>

      {/* Servicios Relacionados */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Servicios Complementarios
            </h2>
            <p className="text-xl text-gray-600">
              Potencia tu SEO con estos servicios relacionados
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Schema Markup */}
            <Link 
              to="/servicios/schema-markup-avanzado"
              className="group bg-gradient-to-br from-slate-50 to-gray-50 rounded-2xl p-8 hover:shadow-2xl transition-all duration-300 border border-gray-200 hover:border-cyan-400"
            >
              <div className="w-16 h-16 bg-gradient-to-r from-cyan-400 to-pink-500 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                <i className="ri-code-s-slash-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4 group-hover:text-cyan-400 transition-colors">
                Schema Markup Avanzado
              </h3>
              <p className="text-gray-600 mb-4">
                Complementa tu arquitectura semántica con datos estructurados que los buscadores y agentes IA entienden perfectamente.
              </p>
              <div className="flex items-center text-cyan-400 font-medium group-hover:gap-3 transition-all">
                <span>Descubrir más</span>
                <i className="ri-arrow-right-line group-hover:translate-x-2 transition-transform"></i>
              </div>
            </Link>

            {/* SEO Agentes IA */}
            <Link 
              to="/servicios/seo-agentes-ia"
              className="group bg-gradient-to-br from-slate-50 to-gray-50 rounded-2xl p-8 hover:shadow-2xl transition-all duration-300 border border-gray-200 hover:border-cyan-400"
            >
              <div className="w-16 h-16 bg-gradient-to-r from-cyan-400 to-pink-500 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                <i className="ri-robot-2-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4 group-hover:text-cyan-400 transition-colors">
                SEO para Agentes IA
              </h3>
              <p className="text-gray-600 mb-4">
                Optimiza tu contenido semántico para que los agentes de IA conversacionales como ChatGPT y Perplexity te encuentren.
              </p>
              <div className="flex items-center text-cyan-400 font-medium group-hover:gap-3 transition-all">
                <span>Descubrir más</span>
                <i className="ri-arrow-right-line group-hover:translate-x-2 transition-transform"></i>
              </div>
            </Link>

            {/* Optimización Extrema */}
            <Link 
              to="/servicios/optimizacion-extrema"
              className="group bg-gradient-to-br from-slate-50 to-gray-50 rounded-2xl p-8 hover:shadow-2xl transition-all duration-300 border border-gray-200 hover:border-cyan-400"
            >
              <div className="w-16 h-16 bg-gradient-to-r from-cyan-400 to-pink-500 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                <i className="ri-flashlight-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4 group-hover:text-cyan-400 transition-colors">
                Optimización Extrema
              </h3>
              <p className="text-gray-600 mb-4">
                Lleva tu arquitectura semántica al máximo rendimiento con optimizaciones técnicas avanzadas de velocidad y Core Web Vitals.
              </p>
              <div className="flex items-center text-cyan-400 font-medium group-hover:gap-3 transition-all">
                <span>Descubrir más</span>
                <i className="ri-arrow-right-line group-hover:translate-x-2 transition-transform"></i>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default SemanticArchitecture;